import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  useGetInboxQuery,
  useGetInboxStatsQuery,
  useUpdateMessageStatusMutation,
  useBulkUpdateMessageStatusMutation,
  useMarkAllAsReadMutation,
  useEmptyTrashMutation,
  useGetMessagePreferencesQuery,
  useUpdateMessagePreferencesMutation,
  type IMessagePreference,
  type IMessage,
} from '../../services/nestjs-base-api';
import {
  Layout,
  Menu,
  List,
  Badge,
  Avatar,
  Typography,
  Button,
  Space,
  Tag,
  Checkbox,
  Input,
  Dropdown,
  Tooltip,
  Empty,
  Modal,
  Switch,
  Form,
  message as antMessage,
} from 'antd';
import {
  InboxOutlined,
  StarOutlined,
  StarFilled,
  DeleteOutlined,
  FolderOutlined,
  MailOutlined,
  ReloadOutlined,
  CheckOutlined,
  SettingOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import { sanitizeHtmlWithLinks } from '../../utils/sanitize';
// Using built-in Date API instead of moment to avoid dependencies

const { Sider, Content } = Layout;
const { Title, Text } = Typography;
const { Search } = Input;

// Date utility functions to replace moment.js
const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) return 'just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
  return date.toLocaleDateString();
};

const formatFullDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

const Messages: React.FC = () => {
  const location = useLocation();

  // State
  const [selectedMessage, setSelectedMessage] = useState<IMessage | null>(null);
  const [selectedFolder, setSelectedFolder] = useState('inbox');
  const [selectedMessages, setSelectedMessages] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [preferencesModalVisible, setPreferencesModalVisible] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });
  const [sortBy, setSortBy] = useState<'date' | 'subject' | 'sender'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [filterType, setFilterType] = useState<string | null>(null);

  // RTK Query hooks
  const { data: inboxData, isLoading: inboxLoading, refetch: refetchInbox } = useGetInboxQuery({
    folder: selectedFolder,
    search: searchQuery || undefined,
    page: pagination.page,
    limit: pagination.limit,
    sortBy,
    sortOrder,
    messageTypeCode: filterType || undefined,
  }, {
    pollingInterval: 30000, // Poll every 30 seconds for new messages
    refetchOnFocus: true,   // Refetch when window regains focus
  });
  const { data: statsData, isLoading: statsLoading, refetch: statsRefetch } = useGetInboxStatsQuery(undefined, {
    pollingInterval: 30000, // Poll every 30 seconds for stats updates
    refetchOnFocus: true,   // Refetch when window regains focus
  });
  const { data: preferencesData, isLoading: preferencesLoading, refetch: preferencesRefetch } = useGetMessagePreferencesQuery(undefined, {
    refetchOnFocus: true,   // Refetch when window regains focus
    refetchOnMountOrArgChange: true, // Always refetch when component mounts
  });
  const [updateMessageStatus] = useUpdateMessageStatusMutation();
  const [bulkUpdateMessageStatus] = useBulkUpdateMessageStatusMutation();
  const [markAllAsRead] = useMarkAllAsReadMutation();
  const [emptyTrash] = useEmptyTrashMutation();
  const [updatePreferences] = useUpdateMessagePreferencesMutation();

  // Derived state
  const messages = inboxData?.messages || [];
  const stats = statsData || {
    total: 0,
    unread: 0,
    starred: 0,
    archived: 0,
  };

  // Update pagination when data changes
  React.useEffect(() => {
    if (inboxData?.pagination) {
      setPagination(prev => ({ ...prev, ...inboxData.pagination }));
    }
  }, [inboxData?.pagination]);

  // Real data from API


  // RTK Query automatically refetches when dependencies change

  // Loading state derived from RTK Query
  const loading = inboxLoading || statsLoading;
  const preferences = preferencesData || [];

  // Check URL parameters to auto-open preferences modal
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('openPreferences') === 'true') {
      preferencesRefetch(); // Refresh preferences data
      setPreferencesModalVisible(true);
      // Clean up URL parameter
      searchParams.delete('openPreferences');
      const newSearch = searchParams.toString();
      window.history.replaceState({}, '', `${location.pathname}${newSearch ? `?${newSearch}` : ''}`);
    }
  }, [location, preferencesRefetch]);

  const fetchMessageDetail = async (hash: string) => {
    // Find message in current list
    const messageRecipient = messages.find(m => m.message.hash === hash);
    if (messageRecipient) {
      setSelectedMessage(messageRecipient.message);

      // Mark as read if it's not already read
      if (!messageRecipient.isRead) {
        try {
          await updateMessageStatus({ hash, updates: { isRead: true } }).unwrap();
          // Refetch both inbox and stats to update the unread count
          refetchInbox();
          // Refetch stats separately to ensure the badge updates
          await statsRefetch();
        } catch (error) {
          console.error('Failed to mark message as read:', error);
        }
      }
    }
  };

  const handleUpdateMessageStatus = async (hash: string, status: any) => {
    try {
      await updateMessageStatus({ hash, updates: status }).unwrap();
      // Force refetch both inbox and stats
      await refetchInbox();
      await statsRefetch();

      // Show appropriate success message and handle navigation
      if (status.isDeleted) {
        antMessage.success('Message deleted successfully');
        setSelectedMessage(null); // Return to message list
      } else if (status.isArchived) {
        antMessage.success('Message archived successfully');
        setSelectedMessage(null); // Return to message list
      } else if (status.isStarred !== undefined) {
        antMessage.success(status.isStarred ? 'Message starred' : 'Message unstarred');
      } else {
        antMessage.success('Message updated');
      }
    } catch (error) {
      console.error('Failed to update message:', error);
      antMessage.error('Failed to update message');
    }
  };

  const bulkUpdateMessages = async (status: any, actionName: string) => {
    if (selectedMessages.length === 0) {
      antMessage.warning('No messages selected');
      return;
    }

    try {
      const messageIds = messages
        .filter(msg => selectedMessages.includes(msg.message.hash))
        .map(msg => msg.message.id);


      await bulkUpdateMessageStatus({ messageIds, status }).unwrap();
      setSelectedMessages([]);
      // Force refetch both inbox and stats
      await refetchInbox();
      await statsRefetch();
      antMessage.success(`Messages ${actionName} successfully`);
    } catch (error) {
      console.error('Failed to update messages:', error);
      antMessage.error('Failed to update messages');
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead().unwrap();
      // Force refetch both inbox and stats
      await refetchInbox();
      await statsRefetch();
      antMessage.success('All messages marked as read');
    } catch (error) {
      console.error('Failed to mark all as read:', error);
      antMessage.error('Failed to mark all as read');
    }
  };

  const handleEmptyTrash = async () => {
    Modal.confirm({
      title: 'Empty Trash',
      content: 'Are you sure you want to permanently delete all messages in trash? This action cannot be undone.',
      okText: 'Empty Trash',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await emptyTrash().unwrap();
          // Force refetch both inbox and stats
          await refetchInbox();
          await statsRefetch();
          antMessage.success('Trash emptied successfully');
        } catch (error) {
          console.error('Failed to empty trash:', error);
          antMessage.error('Failed to empty trash');
        }
      },
    });
  };

  const handleUpdatePreferences = async (newPreferences: any[]) => {
    try {
      await updatePreferences({ preferences: newPreferences }).unwrap();
      antMessage.success('Preferences updated successfully');
    } catch (error) {
      console.error('Failed to update preferences:', error);
      antMessage.error('Failed to update preferences');
    }
  };

  const getPriorityColor = (priority: string) => {
    const colors: any = {
      urgent: 'red',
      high: 'orange',
      normal: 'blue',
      low: 'default',
    };
    return colors[priority] || 'default';
  };

  const getFolderIcon = (folder: string) => {
    const icons: any = {
      inbox: <InboxOutlined />,
      starred: <StarFilled />,
      archived: <FolderOutlined />,
      trash: <DeleteOutlined />,
    };
    return icons[folder] || <InboxOutlined />;
  };

  const renderMessageList = () => (
    <List
      loading={loading}
      itemLayout="horizontal"
      dataSource={messages}
      locale={{ emptyText: <Empty description="No messages found" /> }}
      renderItem={(item: any) => {
        const message = item.message;
        const isSelected = selectedMessages.includes(message.hash);
        const isCurrentMessage = selectedMessage?.hash === message.hash;

        return (
          <List.Item
            className={`message-item ${item.isRead ? '' : 'unread'} ${isCurrentMessage ? 'selected' : ''}`}
            style={{
              cursor: 'pointer',
              backgroundColor: isCurrentMessage ? '#f0f5ff' : isSelected ? '#fafafa' : 'transparent',
              borderLeft: !item.isRead ? '3px solid #F59E0B' : 'none',
              paddingLeft: !item.isRead ? '12px' : '15px',
            }}
            onClick={() => fetchMessageDetail(message.hash)}
            actions={[
              <Tooltip title={item.isStarred ? 'Unstar' : 'Star'}>
                <Button
                  type="text"
                  size="small"
                  icon={item.isStarred ? <StarFilled style={{ color: '#faad14' }} /> : <StarOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleUpdateMessageStatus(message.hash, { isStarred: !item.isStarred });
                  }}
                />
              </Tooltip>,
            ]}
          >
            <List.Item.Meta
              avatar={
                <Space>
                  <Checkbox
                    checked={isSelected}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedMessages([...selectedMessages, message.hash]);
                      } else {
                        setSelectedMessages(selectedMessages.filter(h => h !== message.hash));
                      }
                    }}
                  />
                  <Avatar style={{ backgroundColor: message.messageType?.color || '#F59E0B' }}>
                    {message.senderUser && message.senderUser.firstName && message.senderUser.lastName ?
                      `${message.senderUser.firstName[0]}${message.senderUser.lastName[0]}` :
                      'S'
                    }
                  </Avatar>
                </Space>
              }
              title={
                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                  <Space>
                    <Text strong={!item.isRead} style={{ fontSize: '14px' }}>
                      {message.senderUser && message.senderUser.firstName && message.senderUser.lastName ?
                        `${message.senderUser.firstName} ${message.senderUser.lastName}` :
                        'System'
                      }
                    </Text>
                    {message.priority !== 'normal' && (
                      <Tag color={getPriorityColor(message.priority)} style={{ marginLeft: 8 }}>
                        {message.priority}
                      </Tag>
                    )}
                    {message.messageType && (
                      <Tag color={message.messageType.color} style={{ marginLeft: 4 }}>
                        {message.messageType.name}
                      </Tag>
                    )}
                  </Space>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {formatRelativeTime(message.createdAt)}
                  </Text>
                </Space>
              }
              description={
                <div>
                  <Text strong={!item.isRead} style={{ fontSize: '14px', display: 'block' }}>
                    {message.subject}
                  </Text>
                  <Text type="secondary" style={{ fontSize: '13px' }}>
                    {message.preview}
                  </Text>
                </div>
              }
            />
          </List.Item>
        );
      }}
    />
  );

  const renderMessageDetail = () => {
    if (!selectedMessage) {
      return (
        <Empty
          description="Select a message to view"
          image={<MailOutlined style={{ fontSize: '64px', color: '#d9d9d9' }} />}
        />
      );
    }

    return (
      <div className="message-detail" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '16px', borderBottom: '1px solid #f0f0f0' }}>
          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <Space>
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={async () => {
                  setSelectedMessage(null);
                  // Refetch to ensure we have the latest read status
                  await refetchInbox();
                  await statsRefetch();
                }}
                size="large"
                type="text"
                style={{ marginRight: '8px' }}
              />
              <Title level={4} style={{ margin: 0 }}>{selectedMessage.subject}</Title>
            </Space>
            <Space>
              <Tooltip title="Star">
                <Button
                  type="text"
                  icon={selectedMessage.recipientStatus?.isStarred ?
                    <StarFilled style={{ color: '#faad14' }} /> :
                    <StarOutlined />
                  }
                  onClick={() => handleUpdateMessageStatus(selectedMessage.hash, {
                    isStarred: !selectedMessage.recipientStatus?.isStarred
                  })}
                />
              </Tooltip>
              <Tooltip title="Archive">
                <Button
                  type="text"
                  icon={<FolderOutlined />}
                  onClick={() => handleUpdateMessageStatus(selectedMessage.hash, { isArchived: true })}
                />
              </Tooltip>
              <Tooltip title="Delete">
                <Button
                  type="text"
                  icon={<DeleteOutlined />}
                  onClick={() => handleUpdateMessageStatus(selectedMessage.hash, { isDeleted: true })}
                />
              </Tooltip>
            </Space>
          </Space>
        </div>

        <div style={{ padding: '16px', flex: 1, overflow: 'auto' }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <div style={{ marginBottom: '16px' }}>
              <Space>
                <Avatar style={{ backgroundColor: selectedMessage.messageType?.color || '#F59E0B' }}>
                  {selectedMessage.senderUser && selectedMessage.senderUser.firstName && selectedMessage.senderUser.lastName ?
                    `${selectedMessage.senderUser.firstName[0]}${selectedMessage.senderUser.lastName[0]}` :
                    'S'
                  }
                </Avatar>
                <div>
                  <Text strong>
                    {selectedMessage.senderUser && selectedMessage.senderUser.firstName && selectedMessage.senderUser.lastName ?
                      `${selectedMessage.senderUser.firstName} ${selectedMessage.senderUser.lastName}` :
                      'System'
                    }
                  </Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {formatFullDate(selectedMessage.createdAt)}
                  </Text>
                </div>
              </Space>
              {selectedMessage.messageType && (
                <Tag color={selectedMessage.messageType.color} style={{ marginLeft: '48px', marginTop: '8px' }}>
                  {selectedMessage.messageType.name}
                </Tag>
              )}
            </div>

            <div
              className="message-body"
              dangerouslySetInnerHTML={{
                __html: sanitizeHtmlWithLinks(selectedMessage.bodyHtml || selectedMessage.bodyText)
              }}
              style={{
                padding: '16px',
                backgroundColor: '#fafafa',
                borderRadius: '8px',
                minHeight: '200px',
              }}
            />
          </Space>
        </div>
      </div>
    );
  };

  const renderPreferencesModal = () => (
    <Modal
      title="Message Preferences"
      open={preferencesModalVisible}
      onCancel={() => setPreferencesModalVisible(false)}
      footer={null}
      width={600}
    >
      <Form layout="vertical">
        <Text type="secondary" style={{ display: 'block', marginBottom: '16px' }}>
          Control which types of messages you receive email notifications for. All messages will still appear in your inbox.
        </Text>
        <List
          dataSource={preferences}
          renderItem={(pref: IMessagePreference) => (
            <List.Item>
              <List.Item.Meta
                title={pref.messageType.name}
                description={pref.messageType.description}
              />
              <Space>
                <Tooltip title="Email notifications">
                  <Switch
                    checked={pref.emailEnabled}
                    disabled={pref.messageType.isSystem}
                    onChange={(checked) => {
                      const newPrefs = preferences.map((p: IMessagePreference) =>
                        p.messageType.code === pref.messageType.code
                          ? { ...p, emailEnabled: checked }
                          : p
                      );
                      handleUpdatePreferences(newPrefs.map((p: IMessagePreference) => ({
                        messageTypeCode: p.messageType.code,
                        emailEnabled: p.emailEnabled,
                        inAppEnabled: p.inAppEnabled,
                      })));
                    }}
                  />
                </Tooltip>
              </Space>
            </List.Item>
          )}
        />
      </Form>
    </Modal>
  );

  return (
    <Layout style={{ height: 'calc(100vh - 200px)', background: '#fff' }}>
      <Sider
        width={250}
        style={{ background: '#fff', borderRight: '1px solid #f0f0f0' }}
        breakpoint="md"
        collapsedWidth={0}
      >
        <Menu
          mode="inline"
          selectedKeys={[selectedFolder]}
          onClick={({ key }) => {
            setSelectedFolder(key);
            setPagination({ ...pagination, page: 1 });
          }}
          style={{ marginTop: '16px' }}
          items={[
            {
              key: 'inbox',
              icon: <InboxOutlined />,
              label: (
                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                  <span>Inbox</span>
                  {stats.unread > 0 && <Badge count={stats.unread} />}
                </Space>
              ),
            },
            {
              key: 'starred',
              icon: <StarOutlined />,
              label: (
                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                  <span>Starred</span>
                  {stats.starred > 0 && <Text type="secondary">{stats.starred}</Text>}
                </Space>
              ),
            },
            {
              key: 'archived',
              icon: <FolderOutlined />,
              label: (
                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                  <span>Archived</span>
                  {stats.archived > 0 && <Text type="secondary">{stats.archived}</Text>}
                </Space>
              ),
            },
            {
              key: 'trash',
              icon: <DeleteOutlined />,
              label: 'Trash',
            },
          ]}
        />

        <div style={{ padding: '16px', borderTop: '1px solid #f0f0f0', marginTop: 'auto' }}>
          <Button
            type="text"
            block
            icon={<SettingOutlined />}
            onClick={() => {
              preferencesRefetch(); // Refresh preferences when modal opens
              setPreferencesModalVisible(true);
            }}
          >
            Preferences
          </Button>
        </div>
      </Sider>

      <Layout>
        <div style={{ padding: '8px 16px', borderBottom: '1px solid #f0f0f0', background: '#fafafa' }}>
          <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
            <Space wrap>
              <Search
                placeholder="Search messages..."
                style={{ width: 200 }}
                onSearch={(value) => {
                  setSearchQuery(value);
                  setPagination({ ...pagination, page: 1 });
                }}
              />
              <Button icon={<ReloadOutlined />} onClick={() => refetchInbox()}>
                Refresh
              </Button>
              {selectedMessages.length > 0 && (
                <>
                  <Button onClick={() => bulkUpdateMessages({ isRead: true }, 'marked as read')}>
                    Mark as Read
                  </Button>
                  <Button onClick={() => bulkUpdateMessages({ isArchived: true }, 'archived')}>
                    Archive
                  </Button>
                  <Button onClick={() => bulkUpdateMessages({ isDeleted: true }, 'deleted')}>
                    Delete
                  </Button>
                </>
              )}
              {stats.unread > 0 && selectedMessages.length === 0 && selectedFolder === 'inbox' && (
                <Button onClick={handleMarkAllAsRead} icon={<CheckOutlined />}>
                  Mark All Read
                </Button>
              )}
              {selectedFolder === 'trash' && selectedMessages.length === 0 && (
                <Button onClick={handleEmptyTrash} icon={<DeleteOutlined />} danger>
                  Empty Trash
                </Button>
              )}
            </Space>
            <Space>
              <Dropdown
                menu={{
                  items: [
                    { key: 'date', label: 'Date' },
                    { key: 'subject', label: 'Subject' },
                    { key: 'sender', label: 'Sender' },
                  ],
                  onClick: ({ key }) => setSortBy(key as any),
                }}
              >
                <Button icon={<SortAscendingOutlined />}>
                  Sort: {sortBy}
                </Button>
              </Dropdown>
              <Button
                icon={sortOrder === 'asc' ? <SortAscendingOutlined /> : <SortDescendingOutlined />}
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              />
            </Space>
          </Space>
        </div>

        <Layout style={{ background: '#fff' }}>
          {!selectedMessage ? (
            <Content style={{ flex: 1, overflow: 'auto' }}>
              {renderMessageList()}
            </Content>
          ) : (
            <Content style={{ flex: 1, overflow: 'auto' }}>
              {renderMessageDetail()}
            </Content>
          )}
        </Layout>
      </Layout>

      {renderPreferencesModal()}

      <style>{`
        .message-item:hover {
          background-color: #f5f5f5 !important;
        }
        .message-item.unread {
          font-weight: 600;
        }
        .message-body img {
          max-width: 100%;
          height: auto;
        }
        .message-body a {
          color: #F59E0B;
        }
        /* Better paragraph spacing for readability */
        .message-body p {
          margin: 0 0 1em 0;
          line-height: 1.6;
        }
        .message-body p:last-child {
          margin-bottom: 0;
        }
        /* Hide empty paragraphs */
        .message-body p:empty {
          margin: 0;
          padding: 0;
          height: 0;
        }
        /* Add proper spacing after empty paragraphs */
        .message-body p:empty + p {
          margin-top: 1em;
        }
        .message-body ul, .message-body ol {
          margin: 1em 0;
          padding-left: 2em;
        }
        .message-body li {
          margin: 0.5em 0;
          line-height: 1.6;
        }
      `}</style>
    </Layout>
  );
};

export default Messages;